import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MagnifyingGlass } from '@phosphor-icons/react';
import FlippexLoader from '../components/ui/FlippexLoader';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { StoryCard } from '../components/news/StoryCard';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const NewsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [search, setSearch] = useState(searchParams.get('q') || '');

  useEffect(() => {
    const load = async () => {
      try {
        // The brand loader needs a beat to read; hold it for a second even on a fast response.
        const [postsRes, catsRes] = await Promise.all([
          axios.get(`${API}/api/posts`),
          axios.get(`${API}/api/posts/categories`),
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);
        setPosts(Array.isArray(postsRes.data) ? postsRes.data : []);
        setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (search) params.set('q', search);
    setSearchParams(params, { replace: true });
  }, [category, search, setSearchParams]);

  const tabs = useMemo(() => {
    const present = new Set(posts.map((p) => p.category));
    return [
      { slug: 'all', label: 'All' },
      ...categories.filter((c) => present.has(c.slug)).map((c) => ({ slug: c.slug, label: c.label })),
    ];
  }, [categories, posts]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return posts.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (term && !`${p.title} ${p.excerpt}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [posts, category, search]);

  const featured = category === 'all' && !search ? visible.find((p) => p.is_featured) || visible[0] : null;
  const rest = featured ? visible.filter((p) => p.id !== featured.id) : visible;

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="news-page">
      <Header />

      <div className="pt-8 pb-16 md:pt-14 md:pb-24">
        <div className="mx-auto w-full max-w-[1280px] px-6 md:px-10">
          <header className="mb-6 md:mb-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-muted">News &amp; Stories</p>
            <h1 className="text-3xl md:text-5xl font-heading font-semibold tracking-tight mt-2 md:mt-3">
              What we are building, deal by deal.
            </h1>
            <p className="text-sm md:text-base text-foreground-muted mt-2 md:mt-3">
              Project updates, market reads and company news from the Flippex team.
            </p>
          </header>

          <div
            className="sticky top-16 z-30 -mx-6 md:-mx-10 px-6 md:px-10 pt-4 pb-4 bg-[rgb(var(--background))] border-b border-black/[0.05]"
            data-testid="news-sticky-filters"
          >
            <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide" data-testid="category-selector">
              {tabs.map((tab) => (
                <button
                  key={tab.slug}
                  onClick={() => setCategory(tab.slug)}
                  className={`px-3.5 h-[29px] rounded-full text-[13px] whitespace-nowrap transition-colors ${
                    category === tab.slug
                      ? 'bg-[rgb(var(--foreground))] text-white'
                      : 'bg-white border border-black/[0.08] text-foreground-muted hover:border-black/20'
                  }`}
                  data-testid={`category-tab-${tab.slug}`}
                >
                  {tab.label}
                </button>
              ))}
              {!loading && visible.length > 0 && (
                <p className="ml-1 text-xs text-foreground-muted whitespace-nowrap" data-testid="news-results-count">
                  Showing {visible.length} {visible.length === 1 ? 'story' : 'stories'}
                </p>
              )}
            </div>

            <div className="relative max-w-[320px]">
              <MagnifyingGlass size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stories..."
                aria-label="Search stories"
                className="w-full h-[35px] pl-10 pr-4 rounded-full bg-white border border-black/[0.08] text-[13px]
                  placeholder:text-foreground-muted focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
                data-testid="news-search-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-24 flex justify-center" data-testid="news-loading">
              <FlippexLoader size={48} />
            </div>
          ) : visible.length === 0 ? (
            <div className="py-20 text-center" data-testid="news-empty-state">
              <h3 className="text-xl font-heading font-semibold">
                {posts.length === 0 ? 'Nothing published yet.' : 'No stories match this.'}
              </h3>
              <p className="text-sm text-foreground-muted mt-2 max-w-md mx-auto">
                {posts.length === 0
                  ? 'The first stories are on their way — project updates and market reads.'
                  : 'Try another category or clear your search.'}
              </p>
              {posts.length > 0 && (
                <Button
                  onClick={() => {
                    setCategory('all');
                    setSearch('');
                  }}
                  className="mt-6 rounded-full h-11 px-6 bg-primary hover:bg-primary-hover text-white"
                  data-testid="news-clear-filters"
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="mt-4 md:mt-5 space-y-5 md:space-y-6">
              {featured && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                  <StoryCard post={featured} featured />
                </motion.div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6" data-testid="story-grid">
                {rest.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: Math.min(index * 0.06, 0.3) }}
                  >
                    <StoryCard post={post} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NewsPage;
